import React, { useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Input,
  Textarea,
  Avatar,
} from '../../../components';
import { UserProfileForm } from '../types';
import { validatePhoneNumber } from '@utils/phoneValidation';

interface AccountInformationTabProps {
  profile: UserProfileForm;
  onUpdateProfile: (updates: Partial<UserProfileForm>) => void;
  onUploadAvatar: (file: File) => Promise<{ success: boolean; error?: string }>;
  isSaving: boolean;
}

export const AccountInformationTab: React.FC<AccountInformationTabProps> = ({
  profile,
  onUpdateProfile,
  onUploadAvatar,
  isSaving,
}) => {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [phoneError, setPhoneError] = useState<string | undefined>(undefined);

  const handleAvatarUpload = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        alert(t('profile.pleaseSelectImageFile'));
        return;
      }

      // Validate file size (max 5MB)
      const maxSize = 5 * 1024 * 1024; // 5MB in bytes
      if (file.size > maxSize) {
        alert(t('profile.fileSizeMustBeLessThan5MB'));
        return;
      }

      await onUploadAvatar(file);
    }
  };

  const handlePhoneChange = (value: string) => {
    // Update the profile
    onUpdateProfile({ phone: value });

    // Validate phone number
    const validation = validatePhoneNumber(value, false);
    if (!validation.isValid && validation.error) {
      // Map error messages to translation keys
      let errorKey = 'profile.phoneInvalid';
      if (validation.error.includes('letters')) {
        errorKey = 'profile.phoneNoLetters';
      } else if (validation.error.includes('too short')) {
        errorKey = 'profile.phoneTooShort';
      } else if (validation.error.includes('too long')) {
        errorKey = 'profile.phoneTooLong';
      } else if (validation.error.includes('only contain numbers')) {
        errorKey = 'profile.phoneInvalidFormat';
      }
      setPhoneError(t(errorKey));
    } else {
      setPhoneError(undefined);
    }
  };

  // Validate phone on mount if it has a value
  useEffect(() => {
    if (profile.phone) {
      const validation = validatePhoneNumber(profile.phone, false);
      if (!validation.isValid && validation.error) {
        let errorKey = 'profile.phoneInvalid';
        if (validation.error.includes('letters')) {
          errorKey = 'profile.phoneNoLetters';
        } else if (validation.error.includes('too short')) {
          errorKey = 'profile.phoneTooShort';
        } else if (validation.error.includes('too long')) {
          errorKey = 'profile.phoneTooLong';
        } else if (validation.error.includes('only contain numbers')) {
          errorKey = 'profile.phoneInvalidFormat';
        }
        setPhoneError(t(errorKey));
      } else {
        setPhoneError(undefined);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount - validation happens on user input via handlePhoneChange

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>{t('profile.accountInformation')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="space-y-6">
              <div>
                <label
                  htmlFor="profile-photo"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                >
                  {t('profile.profilePhoto')}
                </label>
                <div className="flex items-center gap-4">
                  <Avatar
                    key={profile.avatar} // Force re-render when avatar changes
                    src={profile.avatar}
                    alt={`${profile.firstName} ${profile.lastName}`}
                    size="lg"
                    className="w-20 h-20"
                    fallback={`${profile.firstName?.charAt(0) || ''}${profile.lastName?.charAt(0) || ''}`}
                  />
                  <div>
                    <Button
                      onClick={handleAvatarUpload}
                      variant="primary"
                      disabled={isSaving}
                      size="sm"
                      className="text-white border-0 shadow-lg !border-0"
                      style={{
                        background:
                          'linear-gradient(136deg, #8258c8 0%, #6766c8 31%, #2c84c8 100%)',
                      }}
                    >
                      <span className="material-icons mr-1 text-sm">upload</span>
                      {t('profile.uploadPhoto')}
                    </Button>
                  </div>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  className="hidden"
                />
              </div>
              <div>
                <label
                  htmlFor="firstName"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                >
                  {t('profile.firstName')}
                </label>
                <Input
                  id="firstName"
                  type="text"
                  value={profile.firstName}
                  onChange={e => onUpdateProfile({ firstName: e.target.value })}
                  className="w-full"
                />
              </div>
              <div>
                <label
                  htmlFor="lastName"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                >
                  {t('profile.lastName')}
                </label>
                <Input
                  id="lastName"
                  type="text"
                  value={profile.lastName}
                  onChange={e => onUpdateProfile({ lastName: e.target.value })}
                  className="w-full"
                />
              </div>
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                >
                  {t('profile.email')}
                </label>
                <Input
                  id="email"
                  type="email"
                  value={profile.email}
                  readOnly
                  className="w-full bg-gray-50 dark:bg-gray-800"
                />
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  {t('profile.emailCannotBeChanged')}
                </p>
              </div>
              <div>
                <label
                  htmlFor="phone"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                >
                  {t('profile.phone')}
                </label>
                <Input
                  id="phone"
                  type="tel"
                  value={profile.phone}
                  onChange={e => handlePhoneChange(e.target.value)}
                  className="w-full"
                  errorText={phoneError}
                />
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  {t('profile.phoneFormatHint')}
                </p>
              </div>
            </div>
            <div className="space-y-6">
              <div>
                <label
                  htmlFor="jobTitle"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                >
                  {t('profile.jobTitle')}
                </label>
                <Input
                  id="jobTitle"
                  type="text"
                  value={profile.jobTitle}
                  onChange={e => onUpdateProfile({ jobTitle: e.target.value })}
                  className="w-full"
                />
              </div>
              <div>
                <label
                  htmlFor="address"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                >
                  {t('profile.address')}
                </label>
                <Input
                  id="address"
                  type="text"
                  value={profile.address}
                  onChange={e => onUpdateProfile({ address: e.target.value })}
                  className="w-full"
                />
              </div>
              <div>
                <label
                  htmlFor="bio"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                >
                  {t('profile.bio')}
                </label>
                <Textarea
                  id="bio"
                  value={profile.bio}
                  onChange={e => onUpdateProfile({ bio: e.target.value })}
                  rows={3}
                  placeholder={t('profile.tellUsAboutYourself')}
                  className="w-full"
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
